import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

/** Convert VAPID URL-safe base64 key to Uint8Array (required by pushManager.subscribe) */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const API = API_URL;

/**
 * Handles PWA setup (manifest, theme-color, service worker) and the
 * beforeinstallprompt banner, plus Web Push subscription after login.
 *
 * @param {string|null} token - Deliverer token (null when not logged in)
 * @param {Function} t - i18n helper
 * @returns {{ installPrompt, handleInstall }}
 */
export default function useEstafetaPWA({ token, t }) {
  const [installPrompt, setInstallPrompt] = useState(null);

  // Inject PWA manifest + theme-color + register SW once on mount
  useEffect(() => {
    let link = document.querySelector('link[rel="manifest"][data-estafeta]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      link.href = "/estafeta-manifest.json";
      link.setAttribute("data-estafeta", "true");
      document.head.appendChild(link);
    }

    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = "#059669";

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/estafeta-sw.js").catch(() => {});
    }

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Register Web Push subscription when a token is available
  useEffect(() => {
    if (!token || !("serviceWorker" in navigator) || !("PushManager" in window)) return;

    const registerPush = async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          await axios.post(`${API}/api/v1/push/subscribe`, {
            deliverer_token: token,
            subscription: existing.toJSON(),
          });
          return;
        }
        const res = await axios.get(`${API}/api/v1/push/vapid-key`);
        if (!res.data.public_key) return;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(res.data.public_key),
        });
        await axios.post(`${API}/api/v1/push/subscribe`, {
          deliverer_token: token,
          subscription: sub.toJSON(),
        });
      } catch {}
    };

    registerPush();
  }, [token]);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      toast.success(t("appInstalled"));
      setInstallPrompt(null);
    }
  };

  return { installPrompt, handleInstall };
}
